/**
 * EventLog - Collapsible event log component for displaying audit logs.
 *
 * Displays a list of audit events for a specific entity (game, team, block, court, referee).
 * Used on entity detail pages to help administrators debug issues.
 */

import { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Collapse,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useQuery } from '@tanstack/react-query';

import { auditApi } from '@/api/client';
import type { AuditLog, AuditLogParams } from '@/types';

// Action type colors
const ACTION_COLORS: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  game_start: 'success',
  game_1st: 'info',
  game_2nd: 'info',
  game_end: 'success',
  game_ot: 'warning',
  game_cancel: 'error',
  game_reset: 'warning',
  score_edit: 'warning',
  team_assign: 'info',
  referee_assign: 'info',
  disqualify: 'error',
  court_change: 'info',
  status_change: 'info',
  comment_add: 'default',
  comment_delete: 'default',
};

/**
 * Format timestamp for display.
 */
function formatTimestamp(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleString('fi-FI', {
    day: 'numeric',
    month: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Single event log entry with expandable details.
 */
function EventLogEntry({ log }: { log: AuditLog }) {
  const [expanded, setExpanded] = useState(false);
  const hasData = Object.keys(log.data).length > 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        '&:not(:last-child)': { mb: 1 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        {/* Timestamp */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ minWidth: 110, fontFamily: 'monospace' }}
        >
          {formatTimestamp(log.created_at)}
        </Typography>

        {/* Action chip */}
        <Chip
          label={log.action_display}
          size="small"
          color={ACTION_COLORS[log.action] || 'default'}
          variant="outlined"
          sx={{ minWidth: 100 }}
        />

        {/* Description */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2">{log.description || log.action_display}</Typography>
          {log.user_display && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <PersonIcon fontSize="inherit" sx={{ color: 'text.secondary', fontSize: 14 }} />
              <Typography variant="caption" color="text.secondary">
                {log.user_display}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Expand button for data */}
        {hasData && (
          <Tooltip title={expanded ? 'Hide details' : 'Show details'}>
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ArrowUpIcon /> : <ArrowDownIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Expandable data section */}
      {hasData && (
        <Collapse in={expanded}>
          <Box
            sx={{
              mt: 1.5,
              pt: 1.5,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Event Data
            </Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1,
                bgcolor: 'action.hover',
                borderRadius: 1,
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                overflow: 'auto',
                maxHeight: 200,
              }}
            >
              {JSON.stringify(log.data, null, 2)}
            </Box>
          </Box>
        </Collapse>
      )}
    </Paper>
  );
}

interface EventLogProps {
  /** Filter parameters for fetching logs */
  params: AuditLogParams;
  /** Title to display in the accordion header */
  title?: string;
  /** Whether the accordion should be expanded by default */
  defaultExpanded?: boolean;
}

/**
 * EventLog component - displays audit logs for an entity.
 */
export function EventLog({ params, title = 'Event Log', defaultExpanded = false }: EventLogProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Fetch audit logs
  const {
    data: logs = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['auditLogs', params],
    queryFn: () => auditApi.list(params),
    enabled: expanded, // Only fetch when expanded
    staleTime: 30000, // 30 seconds
  });

  const handleExpansionChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
    if (isExpanded) {
      refetch();
    }
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={handleExpansionChange}
      variant="outlined"
      sx={{ mt: 3 }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            gap: 1,
          },
        }}
      >
        <HistoryIcon color="action" />
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
        {logs.length > 0 && (
          <Chip label={logs.length} size="small" color="default" sx={{ ml: 1 }} />
        )}
      </AccordionSummary>

      <AccordionDetails>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Alert severity="error">Failed to load event log</Alert>
        ) : logs.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" py={2}>
            No events recorded yet
          </Typography>
        ) : (
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {logs.map((log) => (
              <EventLogEntry key={log.id} log={log} />
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

